import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import {useTranslation} from 'react-i18next';

import {
    formatIdFilterNames,
    formatSourceList,
    formatStatusList,
    hasSourceFilter,
    isSourceFilterEmpty,
    readSectorFilter,
    readServiceFilter,
    readSources,
    readStatuses
} from '../../../config/reservationTriggers';
import NodeEditorLayout from '../NodeEditorLayout';
import {NodeEditorV2Props} from '../types';
import ReservationStatusMultiSelectField from './ReservationStatusMultiSelectField';
import ReservationTriggerFilters from './ReservationTriggerFilters';

const ReservationStatusChangedEditorV2 = ({nodeConfig, setNodeConfig, readOnly}: NodeEditorV2Props) => {
    const {t} = useTranslation();

    const sources = readSources(nodeConfig);
    const statuses = readStatuses(nodeConfig);
    const includeCreated = !!nodeConfig.includeCreated;
    // From the stored snapshots; an empty filter means any and adds no clause.
    const sectorName = formatIdFilterNames(readSectorFilter(nodeConfig), t);
    const serviceName = formatIdFilterNames(readServiceFilter(nodeConfig), t);

    const noSources = isSourceFilterEmpty(sources);

    const summary = () => {
        if (noSources) return t('No source is ticked, so this trigger never fires.');

        const head = statuses.length
            ? t('Runs whenever a reservation reaches one of these statuses: {{statuses}}.', {statuses: formatStatusList(statuses, t)})
            : t('Runs on every reservation status change.');

        const created = includeCreated
            ? (statuses.length
                ? t('It also runs when a reservation is created directly in one of them.')
                : t('It also runs when a reservation is created.'))
            : '';

        const sourceClause = hasSourceFilter(sources)
            ? t('Only reservations from these sources count: {{sources}}.', {sources: formatSourceList(sources!, t)})
            : '';

        const scope = (() => {
            if (sectorName && serviceName) {
                return t('Only in {{sector}}, booked as {{service}}.', {sector: sectorName, service: serviceName});
            }
            if (sectorName) return t('Only in {{sector}}.', {sector: sectorName});
            if (serviceName) return t('Only booked as {{service}}.', {service: serviceName});
            return '';
        })();

        const tail = t('The reservation, the status it left and the status it reached are available to the steps below.');

        return [head, created, sourceClause, scope, tail].filter(Boolean).join(' ');
    };

    return (
        <NodeEditorLayout state={noSources ? 'wontRun' : 'configured'} summary={summary()}>
            <ReservationStatusMultiSelectField
                label={t('Only these statuses')}
                placeholder={t('Any status')}
                value={statuses}
                onChange={values => setNodeConfig({...nodeConfig, statuses: values})}
                disabled={readOnly}
            />

            <FormControlLabel
                control={
                    <Switch
                        size={'small'}
                        checked={includeCreated}
                        onChange={event => setNodeConfig({...nodeConfig, includeCreated: event.target.checked})}
                        disabled={readOnly}
                    />
                }
                label={t('Also run when a reservation is created directly in one of these statuses')}
                slotProps={{typography: {variant: 'body1'}}}
            />

            <ReservationTriggerFilters
                nodeConfig={nodeConfig}
                setNodeConfig={setNodeConfig}
                readOnly={readOnly}
            />
        </NodeEditorLayout>
    );
};

export default ReservationStatusChangedEditorV2;
